import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { money } from '@/lib/utils';
import { Input } from '@/components/ui/primitives';
import { useDebounced } from '../diagnostics/LabBenchDebounce';

export interface ServiceOption {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
}

const SEARCH_LIMIT = 20;

export function ServiceSearch({ onChoose, autoFocus }: { onChoose: (service: ServiceOption) => void; autoFocus?: boolean }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(Boolean(autoFocus));
  const searched = useDebounced(query.trim(), 300);

  const results = useQuery({
    queryKey: ['charge-items-search', searched],
    queryFn: async () => (await get<{ data: ServiceOption[] }>(`/charge-items${qs({ q: searched || undefined, limit: SEARCH_LIMIT })}`)).data,
    enabled: focused || searched.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const options = results.data ?? [];
  const showList = focused || query.trim().length > 0;

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={tr('billing', 'search_services_by_name_or_code')}
        autoComplete="off"
        autoFocus={autoFocus}
        aria-label={tr('billing', 'select_a_service')}
      />
      {showList && (
        <div className="mt-1 max-h-56 overflow-y-auto rounded-md border bg-background">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-accent/50"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChoose(option);
                setQuery('');
                setFocused(false);
              }}
            >
              <span className="min-w-0 truncate">
                {option.name} <span className="text-xs text-muted-foreground">({option.code})</span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground tabular">
                {tr('billing', 'list_price')} {money(option.basePriceMinor)}
              </span>
            </button>
          ))}
          {results.isSuccess && options.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {searched ? tr('billing', 'no_service_matches_x', { value: searched }) : tr('billing', 'nothing_is_in_the_charge_master_yet_over_the')}
            </p>
          )}
          {options.length === SEARCH_LIMIT && <p className="px-3 py-1.5 text-xs text-muted-foreground">{tr('billing', 'type_more_to_narrow_the_list')}</p>}
        </div>
      )}
    </div>
  );
}
